import { Mtds } from 'prove-common'
import { formatCount, formatPercent } from './statisticsFormat'

const SummaryMetricCard = ({
  value,
  percent,
  label,
  accent = 'text-text-primary',
  helpText,
  className = ''
}: {
  value: string
  percent?: string
  label: string
  accent?: string
  helpText?: string
  className?: string
}) => (
  <Mtds.Card className={`w-full ${className}`}>
    <Mtds.Prose className='min-w-0'>
      <Mtds.Info>
        <strong>{label}</strong>
        <Mtds.Heading
          data-size='2xl'
          as={Mtds.Flex}
          data-align='center'
          data-gap={2}
          className={accent}
        >
          <span>{value}</span>
          {percent != null && (
            <Mtds.Prose data-size='2xs'>{percent}</Mtds.Prose>
          )}
        </Mtds.Heading>
      </Mtds.Info>
      {helpText && <Mtds.Muted data-size='2xs'>{helpText}</Mtds.Muted>}
    </Mtds.Prose>
  </Mtds.Card>
)

export const getSummaryMetricTitle = (
  isCurrentReportingYear: boolean,
  selectedPlanYear: number
) => (isCurrentReportingYear ? 'Totalt i år' : `Totalt for ${selectedPlanYear}`)

export const SummaryMetricCardItems = ({
  samplesToTakePlan,
  samplesTakenPlan,
  completionPercent,
  targetMetricsApplicable = true,
  targetYear,
  unavailableHelpText,
  className
}: {
  samplesToTakePlan: number
  samplesTakenPlan: number
  completionPercent: number
  targetMetricsApplicable?: boolean
  targetYear?: number
  unavailableHelpText?: string
  className?: string
}) => {
  const remainingSamples = Math.max(samplesToTakePlan - samplesTakenPlan, 0)
  const targetHelpText = !targetMetricsApplicable
    ? (unavailableHelpText ??
      (targetYear != null
        ? `Planens mål gjelder for ${targetYear}.`
        : undefined))
    : undefined

  return (
    <>
      <SummaryMetricCard
        label='Planlagt'
        value={targetMetricsApplicable ? formatCount(samplesToTakePlan) : '–'}
        helpText={targetHelpText}
        className={className}
      />
      <SummaryMetricCard
        label='Tatt'
        value={formatCount(samplesTakenPlan)}
        accent='text-warning-text-default'
        className={className}
      />
      <SummaryMetricCard
        label='Gjenstår'
        value={targetMetricsApplicable ? formatCount(remainingSamples) : '–'}
        accent={
          targetMetricsApplicable && remainingSamples > 0
            ? 'text-danger-text-default'
            : 'text-text-primary'
        }
        className={className}
      />
      <SummaryMetricCard
        label='Måloppnåelse'
        value={targetMetricsApplicable ? formatPercent(completionPercent) : '–'}
        accent={
          !targetMetricsApplicable || completionPercent >= 100
            ? 'text-text-primary'
            : 'text-danger-text-default'
        }
        className={className}
      />
    </>
  )
}

export const SummaryMetricCards = ({
  isCurrentReportingYear,
  selectedPlanYear,
  samplesToTakePlan,
  samplesTakenPlan,
  completionPercent
}: {
  isCurrentReportingYear: boolean
  selectedPlanYear: number
  samplesToTakePlan: number
  samplesTakenPlan: number
  completionPercent: number
}) => {
  const totalsHeading = getSummaryMetricTitle(
    isCurrentReportingYear,
    selectedPlanYear
  )

  return (
    <Mtds.Grid>
      <Mtds.Grid>
        <Mtds.Heading data-size='sm'>{totalsHeading}</Mtds.Heading>
        <Mtds.Grid data-items={250}>
          <SummaryMetricCardItems
            samplesToTakePlan={samplesToTakePlan}
            samplesTakenPlan={samplesTakenPlan}
            completionPercent={completionPercent}
          />
        </Mtds.Grid>
      </Mtds.Grid>
    </Mtds.Grid>
  )
}
